import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button.js';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.js';
// import { Badge } from '@/components/ui/badge.js'; // DISABLED: Inbox feature
import {
  MessageSquare,
  // Inbox, // DISABLED: Inbox feature
  // Users, // DISABLED: Contacts feature
  Plus,
  Calendar, // Enabled: Routines feature
  // Command, // DISABLED: Prompts feature
  // Package, // DISABLED: Artifacts feature
  Moon,
  Sun,
} from 'lucide-react';
import { cn } from '@/lib/utils.js';
// DISABLED: Inbox feature
// import { useApprovalStore } from '@/stores/approvalStore.js';
// import { useTasksStore } from '@/stores/tasksStore.js';
// Enabled: Routines feature
import { useSchedulerStore } from '@/stores/schedulerStore.js';
import { useServices } from '@/contexts/ServicesContext.js';
// import { useAskUserStore } from '@/stores/askUserStore.js'; // DISABLED: Inbox feature
// import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js'; // DISABLED: Artifacts feature
import { useChatTabsStore } from '@/stores/chatTabsStore.js';
import { UserMenu } from '@/components/auth/UserMenu.js';
import { useTheme } from '@/contexts/ThemeContext.js';
import { NotificationsDropdown } from '@/components/sidebar/NotificationsDropdown.js';

interface SidebarProps {
  activePanel: 'conversations' | 'inbox' | 'contacts' | 'schedules' | null;
  onPanelChange: (panel: 'conversations' | 'inbox' | 'contacts' | 'schedules' | null) => void;
  activeOverlay?: 'notes' | 'memories' | 'prompts' | null;
  onOverlayChange?: (overlay: 'notes' | 'memories' | 'prompts' | null) => void;
  children?: React.ReactNode;
}

export function Sidebar({ activePanel, onPanelChange, activeOverlay: _activeOverlay, onOverlayChange, children }: SidebarProps) {
  const navigate = useNavigate();
  const { sessionService } = useServices();
  const addTab = useChatTabsStore((state) => state.addTab);
  const { theme, setTheme, effectiveTheme } = useTheme();

  // DISABLED: Inbox feature
  // const pendingApprovalsCount = useApprovalStore((state) => state.getPendingCount());
  // const pendingTasksCount = useTasksStore((state) => state.getPendingIncomingCount());
  // const pendingPromptsCount = useAskUserStore((state) => state.getPendingCount());
  // const totalPendingCount = pendingApprovalsCount + pendingTasksCount + pendingPromptsCount;
  // Enabled: Routines feature
  const routinesRunningCount = useSchedulerStore((state) => state.getRunningCount());

  const handleToggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('light');
    } else {
      // If system, toggle to the opposite of current effective theme
      setTheme(effectiveTheme === 'dark' ? 'light' : 'dark');
    }
  };

  // DISABLED: Artifacts feature
  // const { openNotesList, isOpen: artifactPanelOpen, togglePanel } = useArtifactPanelStore();

  const handleNewChat = () => {
    // Generate unique ID for each new welcome tab (matching ChatTabBar behavior)
    const uniqueId = `welcome-${Date.now()}`;
    addTab({
      id: uniqueId,
      title: 'New Chat',
      sessionId: null,
      isPinned: false,
    });
    sessionService.returnToWelcome();
    navigate('/');
    onPanelChange(null);
    onOverlayChange?.(null);
  };

  const handlePanelClick = (panel: 'conversations' | 'inbox' | 'contacts' | 'schedules') => {
    // Toggle panel open/close - don't navigate
    onPanelChange(activePanel === panel ? null : panel);
  };

  // DISABLED: Artifacts feature
  // const handleArtifactsClick = () => {
  //   if (!artifactPanelOpen) {
  //     openNotesList(); // Open notes list by default
  //   } else {
  //     togglePanel(); // Toggle the panel off if already open
  //   }
  //   onPanelChange(null); // Close sidebar panel if opening artifact panel
  // };

  // DISABLED: Prompts feature
  // const handlePromptsClick = () => {
  //   onOverlayChange?.(activeOverlay === 'prompts' ? null : 'prompts');
  //   onPanelChange(null); // Close sidebar panel if opening overlay
  // };

  return (
    <div className="flex h-full">
      {/* Icon Bar */}
      <div className="w-14 bg-muted/50 flex flex-col items-center py-3 gap-1 border-r border-border/30">
        {/* New Chat Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNewChat}
              className="text-primary hover:bg-primary/10 rounded"
            >
              <Plus className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">New Chat</TooltipContent>
        </Tooltip>

        {/* Notifications - Scheduler Activity */}
        <NotificationsDropdown />

        {/* Conversations Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handlePanelClick('conversations')}
              className={cn(
                'relative rounded',
                activePanel === 'conversations' && 'bg-primary/10 text-primary'
              )}
            >
              <MessageSquare className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Conversations</TooltipContent>
        </Tooltip>

        {/* DISABLED: Inbox feature */}
        {/* <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={activePanel === 'inbox' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => handlePanelClick('inbox')}
              className={cn(
                'relative',
                activePanel === 'inbox' && 'bg-primary/10 text-primary hover:bg-primary/20'
              )}
            >
              <Inbox className="h-5 w-5" />
              {totalPendingCount > 0 && (
                <Badge
                  variant="destructive"
                  className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-xs"
                >
                  {totalPendingCount}
                </Badge>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Inbox</TooltipContent>
        </Tooltip> */}

        {/* DISABLED: Contacts requires /users/:id/agent-card endpoint not available on multi-agent-server */}
        {/* <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={activePanel === 'contacts' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={() => handlePanelClick('contacts')}
              className={cn(
                'relative',
                activePanel === 'contacts' && 'bg-primary/10 text-primary hover:bg-primary/20'
              )}
            >
              <Users className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Contacts</TooltipContent>
        </Tooltip> */}

        {/* Routines Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handlePanelClick('schedules')}
              className={cn(
                'relative rounded',
                activePanel === 'schedules' && 'bg-primary/10 text-primary'
              )}
            >
              <Calendar className="h-5 w-5" />
              {routinesRunningCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Routines</TooltipContent>
        </Tooltip>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Theme Toggle */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleToggleTheme}
              className="rounded text-muted-foreground hover:text-foreground"
            >
              {effectiveTheme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {effectiveTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </TooltipContent>
        </Tooltip>

        {/* DISABLED: Artifacts feature */}
        {/* <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={artifactPanelOpen ? 'secondary' : 'ghost'}
              size="icon"
              onClick={handleArtifactsClick}
              className={cn(
                'relative',
                artifactPanelOpen && 'bg-primary/10 text-primary hover:bg-primary/20'
              )}
            >
              <Package className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Artifacts</TooltipContent>
        </Tooltip> */}

        {/* DISABLED: Prompts feature */}
        {/* <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={activeOverlay === 'prompts' ? 'secondary' : 'ghost'}
              size="icon"
              onClick={handlePromptsClick}
              className={cn(
                'relative',
                activeOverlay === 'prompts' && 'bg-primary/10 text-primary hover:bg-primary/20'
              )}
            >
              <Command className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Prompts</TooltipContent>
        </Tooltip> */}

        {/* User Menu */}
        <div className="mt-1">
          <UserMenu />
        </div>
      </div>

      {/* Panel Content */}
      {activePanel && (
        <div className="w-72 bg-background border-r border-border/30 animate-slide-in overflow-hidden">
          {children}
        </div>
      )}
    </div>
  );
}
